/**
 * System API Contracts
 *
 * System configuration and analytics endpoints.
 */

import { z } from 'zod'
import { AppConfigSchema, ThemeConfigSchema } from '../../schemas/entities/app-config'
import { GlobalFeaturesConfigSchema, SingleGlobalFeatureConfigSchema } from '../../schemas/entities/global-features'
import { ScreensConfigSchema, ScreenFeatureConfigSchema } from '../../schemas/entities/screen-features'
import {
  ScheduledTaskSchema,
  ScheduledTaskEndpointSchema,
  ScheduledTaskHttpMethodSchema,
  ScheduledTaskExecutionSchema,
  ScheduledTaskExecutionStatusSchema,
  ScheduledTaskDomainSchema,
  SCHEDULED_TASK_DOMAINS
} from '../../schemas/entities/scheduled-task'
import {
  EventSeveritySchema,
  EventLogActorSchema,
  EventLogSchoolSchema,
  EventLogSchema
} from '../../schemas/entities/system-event-log'

// Re-export for convenience
export { AppConfigSchema, ThemeConfigSchema }
export type { AppConfig, ThemeConfig } from '../../schemas/entities/app-config'
export { GlobalFeaturesConfigSchema, SingleGlobalFeatureConfigSchema }
export type { GlobalFeaturesConfig, SingleGlobalFeatureConfig } from '../../schemas/entities/global-features'
export { ScreensConfigSchema, ScreenFeatureConfigSchema }
export type { ScreensConfig, ScreenFeatureConfig } from '../../schemas/entities/screen-features'

export { ScheduledTaskSchema, ScheduledTaskEndpointSchema, ScheduledTaskHttpMethodSchema }
export { ScheduledTaskExecutionSchema, ScheduledTaskExecutionStatusSchema, ScheduledTaskDomainSchema }
export { SCHEDULED_TASK_DOMAINS }
export type {
  ScheduledTask,
  ScheduledTaskEndpoint,
  ScheduledTaskHttpMethod,
  ScheduledTaskExecution,
  ScheduledTaskExecutionStatus,
  ScheduledTaskDomain
} from '../../schemas/entities/scheduled-task'

export { EventSeveritySchema, EventLogActorSchema, EventLogSchoolSchema, EventLogSchema }
export type { EventSeverity, EventLogActor, EventLogSchool, EventLog } from '../../schemas/entities/system-event-log'

/**
 * GET /v1/system/app-config
 */
export const GetAppConfigRequestSchema = z.object({
  schoolId: z.string().optional()
})

export type GetAppConfigRequest = z.infer<typeof GetAppConfigRequestSchema>

export const GetAppConfigResponseSchema = z.object({
  data: AppConfigSchema,
  message: z.string()
})

export type GetAppConfigResponse = z.infer<typeof GetAppConfigResponseSchema>

/**
 * PATCH /v1/system/app-config
 */
export const UpdateAppConfigRequestSchema = AppConfigSchema.partial()

export type UpdateAppConfigRequest = z.infer<typeof UpdateAppConfigRequestSchema>

export const UpdateAppConfigResponseSchema = z.object({
  data: AppConfigSchema,
  message: z.string()
})

export type UpdateAppConfigResponse = z.infer<typeof UpdateAppConfigResponseSchema>

/**
 * GET /v1/system/app-config/theme
 */
export const GetThemeConfigResponseSchema = z.object({
  data: ThemeConfigSchema,
  message: z.string()
})

export type GetThemeConfigResponse = z.infer<typeof GetThemeConfigResponseSchema>

/**
 * PATCH /v1/system/app-config/theme
 */
export const UpdateThemeConfigRequestSchema = ThemeConfigSchema.partial()

export type UpdateThemeConfigRequest = z.infer<typeof UpdateThemeConfigRequestSchema>

/**
 * GET /v1/system/app-config/global-features
 */
export const GetGlobalFeaturesResponseSchema = z.object({
  data: GlobalFeaturesConfigSchema,
  message: z.string()
})

export type GetGlobalFeaturesResponse = z.infer<typeof GetGlobalFeaturesResponseSchema>

/**
 * GET /v1/system/app-config/global-features/:featureName
 */
export const GetFeatureConfigResponseSchema = z.object({
  data: SingleGlobalFeatureConfigSchema,
  message: z.string()
})

export type GetFeatureConfigResponse = z.infer<typeof GetFeatureConfigResponseSchema>

/**
 * PATCH /v1/system/app-config/global-features/:featureName
 */
export const UpdateFeatureConfigRequestSchema = SingleGlobalFeatureConfigSchema.partial()

export type UpdateFeatureConfigRequest = z.infer<typeof UpdateFeatureConfigRequestSchema>

/**
 * GET /v1/system/app-config/screen-features
 */
export const GetScreenFeaturesResponseSchema = z.object({
  data: ScreensConfigSchema,
  message: z.string()
})

export type GetScreenFeaturesResponse = z.infer<typeof GetScreenFeaturesResponseSchema>

/**
 * Dashboard Analytics
 */
export const DashboardStatsSchema = z.object({
  totalUsers: z.number(),
  activeUsers: z.number(),
  totalPosts: z.number(),
  totalEvents: z.number(),
  totalAnnouncements: z.number(),
  engagementRate: z.number().optional(),
  userGrowth: z.number().optional(),
  topGroups: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        memberCount: z.number()
      })
    )
    .optional()
})

export type DashboardStats = z.infer<typeof DashboardStatsSchema>

/**
 * GET /v1/system/analytics/school/:schoolId/dashboard
 */
export const GetSchoolDashboardResponseSchema = z.object({
  data: DashboardStatsSchema,
  message: z.string()
})

export type GetSchoolDashboardResponse = z.infer<typeof GetSchoolDashboardResponseSchema>

/**
 * GET /v1/system/analytics/admin/dashboard
 */
const UserStatusSchema = z.enum(['PLATFORM_ONLY', 'PENDING', 'ACTIVE', 'INACTIVE', 'SUSPENDED', 'BLOCKED', 'DELETED'])

const RoleBreakdownSchema = z.object({
  students: z.number(),
  parents: z.number(),
  faculty: z.number(),
  other: z.number()
})

const SubscriptionPlanBreakdownSchema = z.object({
  planName: z.string(),
  status: z.string(),
  count: z.number()
})

const DailyActiveUsersTimeSeriesPointSchema = z.object({
  date: z.string(),
  students: z.number(),
  parents: z.number(),
  faculty: z.number()
})

export const AdminDashboardDataSchema = z.object({
  platformOverview: z.object({
    totalSchools: z.number(),
    schoolsBySubscription: z.object({
      withSubscription: z.number(),
      withoutSubscription: z.number(),
      byPlan: z.array(SubscriptionPlanBreakdownSchema)
    }),
    schoolsWithSyncEnabled: z.number()
  }),
  users: z.object({
    platformUsers: z.object({
      total: z.number(),
      byRole: RoleBreakdownSchema,
      byStatus: z.record(UserStatusSchema, z.number())
    }),
    appUsers: z.object({
      total: z.number(),
      withVerifiedEmail: z.number(),
      conversionRate: z.number(),
      byRole: RoleBreakdownSchema,
      byStatus: z.record(z.string(), z.number())
    })
  }),
  dailyActiveUsersTimeSeries: z.array(DailyActiveUsersTimeSeriesPointSchema),
  content: z.object({
    total: z.number(),
    last7Days: z.number(),
    last30Days: z.number()
  }),
  activeUsers: z.object({
    currentlyOnline: z.number(),
    dailyActive: z.number(),
    weeklyActive: z.number()
  }),
  systemHealth: z.object({
    syncJobs: z.object({
      last24Hours: z.number(),
      last7Days: z.number(),
      failedLast7Days: z.number()
    })
  })
})

export type AdminDashboardData = z.infer<typeof AdminDashboardDataSchema>

export const GetAdminDashboardResponseSchema = z.object({
  data: AdminDashboardDataSchema,
  message: z.string()
})

export type GetAdminDashboardResponse = z.infer<typeof GetAdminDashboardResponseSchema>

// ============================================
// Scheduled Tasks (DB + EventBridge)
// ============================================

/**
 * GET /v1/system/scheduled-tasks
 */
export const ListScheduledTasksResponseSchema = z.object({
  data: z.array(ScheduledTaskSchema),
  meta: z.object({
    total: z.number()
  })
})

export type ListScheduledTasksResponse = z.infer<typeof ListScheduledTasksResponseSchema>

/**
 * GET /v1/system/scheduled-tasks/:name
 */
export const GetScheduledTaskResponseSchema = z.object({
  data: ScheduledTaskSchema
})

export type GetScheduledTaskResponse = z.infer<typeof GetScheduledTaskResponseSchema>

/**
 * POST /v1/system/scheduled-tasks
 */
export const CreateScheduledTaskRequestSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, {
      message: 'Name must be kebab-case (lowercase alphanumeric with hyphens)'
    }),
  description: z.string().min(1),
  schedule: z.string().min(1),
  enabled: z.boolean().optional().default(true),
  /**
   * REQUIRED on create, unlike the nullable column and the nullable
   * `ScheduledTaskSchema` field (R11, R14). The column is nullable only so
   * pre-backfill rows stay readable; a task created from here has no such
   * excuse, and an unclassified new row would land in the admin list's
   * fallback group forever. Enforcing it at the contract is what keeps the
   * classification from being a knob nothing consumes.
   */
  domain: ScheduledTaskDomainSchema,
  endpoint: z.object({
    path: z
      .string()
      .min(1)
      .refine(v => v.startsWith('/') || v.startsWith('http://') || v.startsWith('https://'), {
        message: 'Path must start with / (relative) or http(s):// (absolute URL)'
      }),
    method: ScheduledTaskHttpMethodSchema,
    body: z.record(z.string(), z.unknown()).optional(),
    queryParams: z.record(z.string(), z.string()).optional()
  })
})

export type CreateScheduledTaskRequest = z.infer<typeof CreateScheduledTaskRequestSchema>

export const CreateScheduledTaskResponseSchema = z.object({
  data: ScheduledTaskSchema
})

export type CreateScheduledTaskResponse = z.infer<typeof CreateScheduledTaskResponseSchema>

/**
 * PATCH /v1/system/scheduled-tasks/:name
 */
export const UpdateScheduledTaskRequestSchema = z.object({
  schedule: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  description: z.string().optional(),
  /** Optional here only because every field on a PATCH is — it may not be cleared back to null. */
  domain: ScheduledTaskDomainSchema.optional(),
  endpoint: z
    .object({
      path: z
        .string()
        .min(1)
        .refine(v => v.startsWith('/') || v.startsWith('http://') || v.startsWith('https://'), {
          message: 'Path must start with / (relative) or http(s):// (absolute URL)'
        }),
      method: ScheduledTaskHttpMethodSchema,
      body: z.record(z.string(), z.unknown()).optional(),
      queryParams: z.record(z.string(), z.string()).optional()
    })
    .optional()
})

export type UpdateScheduledTaskRequest = z.infer<typeof UpdateScheduledTaskRequestSchema>

export const UpdateScheduledTaskResponseSchema = z.object({
  data: ScheduledTaskSchema
})

export type UpdateScheduledTaskResponse = z.infer<typeof UpdateScheduledTaskResponseSchema>

/**
 * DELETE /v1/system/scheduled-tasks/:name
 */
export const DeleteScheduledTaskResponseSchema = z.object({
  message: z.string()
})

export type DeleteScheduledTaskResponse = z.infer<typeof DeleteScheduledTaskResponseSchema>

/**
 * POST /v1/system/scheduled-tasks/:name/run
 */
export const RunScheduledTaskResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    status: z.number(),
    body: z.unknown()
  })
})

export type RunScheduledTaskResponse = z.infer<typeof RunScheduledTaskResponseSchema>

// ============================================
// Scheduled Task Executions (History)
// ============================================

/**
 * GET /v1/system/scheduled-tasks/executions
 */
export const ListScheduledTaskExecutionsRequestSchema = z.object({
  task_name: z.string().optional(),
  limit: z.coerce.number().min(1).max(500).optional().default(50),
  offset: z.coerce.number().min(0).optional().default(0)
})

export type ListScheduledTaskExecutionsRequest = z.infer<typeof ListScheduledTaskExecutionsRequestSchema>

export const ListScheduledTaskExecutionsResponseSchema = z.object({
  data: z.array(ScheduledTaskExecutionSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})

export type ListScheduledTaskExecutionsResponse = z.infer<typeof ListScheduledTaskExecutionsResponseSchema>

// ============================================
// System Event Logs
// ============================================

/**
 * GET /v1/system/event-logs
 */
export const GetEventLogsRequestSchema = z.object({
  eventType: z.string().optional(),
  severity: EventSeveritySchema.optional(),
  source: z.string().optional(),
  schoolId: z.string().optional(),
  actorId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().min(1).max(100).optional().default(50),
  offset: z.coerce.number().min(0).optional().default(0)
})

export type GetEventLogsRequest = z.infer<typeof GetEventLogsRequestSchema>

export const GetEventLogsResponseSchema = z.object({
  data: z.array(EventLogSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  }),
  message: z.string()
})

export type GetEventLogsResponse = z.infer<typeof GetEventLogsResponseSchema>

// ============================================
// Communications Dashboard
// ============================================

export {
  CommsDashboardRangeSchema,
  CommsDashboardContentTypeSchema,
  COMMS_DASHBOARD_MAX_ACTIVITY_IDS,
  CommsDashboardPostRowSchema,
  CommsDashboardPostsRequestSchema,
  CommsDashboardPostsResponseSchema,
  CommsDashboardCommunityStatsRequestSchema,
  CommsDashboardCommunityStatsResponseSchema,
  CommsDashboardActivityEngagementSchema,
  CommsDashboardEngagementRequestSchema,
  CommsDashboardEngagementResponseSchema,
  CommsDashboardActivityReactionsSchema,
  CommsDashboardReactionsRequestSchema,
  CommsDashboardReactionsResponseSchema,
  CommsDashboardTimelineBucketSchema,
  CommsDashboardTimelineRequestSchema,
  CommsDashboardTimelineResponseSchema
} from './communications-dashboard'

export type {
  CommsDashboardRange,
  CommsDashboardContentType,
  CommsDashboardPostRow,
  CommsDashboardPostsRequest,
  CommsDashboardPostsResponse,
  CommsDashboardCommunityStatsRequest,
  CommsDashboardCommunityStatsResponse,
  CommsDashboardActivityEngagement,
  CommsDashboardEngagementRequest,
  CommsDashboardEngagementResponse,
  CommsDashboardActivityReactions,
  CommsDashboardReactionsRequest,
  CommsDashboardReactionsResponse,
  CommsDashboardTimelineBucket,
  CommsDashboardTimelineRequest,
  CommsDashboardTimelineResponse
} from './communications-dashboard'

// ============================================
// Admin Live Ops Map
// ============================================

export { MapPersonRoleSchema, MapPersonDotSchema, MapStatsByRoleSchema, MapStatsSchema } from './map-data'

export type { MapPersonRole, MapPersonDot, MapStatsByRole, MapStats } from './map-data'

// ============================================
// TV Mission Control Display
// ============================================

export {
  DisplaySchoolPinSchema,
  DisplayMapSchema,
  DisplayActiveUserSchema,
  DisplayActiveSchoolSchema,
  DisplayOnlineUsersSchema,
  DisplayQueuesSchema,
  DisplayWorkersSchema,
  DisplayInfrastructureSchema,
  DisplayRevenueSchema,
  DisplayMissionControlRequestSchema,
  DisplayMissionControlResponseSchema
} from './display-mission-control'

export type {
  DisplaySchoolPin,
  DisplayMap,
  DisplayActiveUser,
  DisplayActiveSchool,
  DisplayOnlineUsers,
  DisplayQueues,
  DisplayWorkers,
  DisplayInfrastructure,
  DisplayRevenue,
  DisplayMissionControlRequest,
  DisplayMissionControlResponse
} from './display-mission-control'

// ============================================
// Revenue & Renewal Metrics
// ============================================

export {
  RevenueAmountSchema,
  RevenueCoverageSchema,
  RevenueFreshFlagSchema,
  RevenueSummaryRequestSchema,
  RevenuePlanBreakdownSchema,
  RevenueMonthClosedSchema,
  RevenueClosedWonLineSchema,
  RevenueSummaryResponseSchema,
  RevenueRenewalWindowSchema,
  RevenueRenewalsRequestSchema,
  RevenueRenewalRowSchema,
  RevenueRenewalsResponseSchema,
  RevenueReconciliationRequestSchema,
  RevenueReconciliationSchoolSchema,
  RevenueUnlinkedSchoolSchema,
  RevenueUnpaidSubscriptionSchema,
  RevenueReconciliationResponseSchema
} from './revenue'

export type {
  RevenueCoverage,
  RevenueSummaryRequest,
  RevenuePlanBreakdown,
  RevenueMonthClosed,
  RevenueClosedWonLine,
  RevenueSummaryResponse,
  RevenueRenewalWindow,
  RevenueRenewalsRequest,
  RevenueRenewalRow,
  RevenueRenewalsResponse,
  RevenueReconciliationRequest,
  RevenueReconciliationSchool,
  RevenueUnlinkedSchool,
  RevenueUnpaidSubscription,
  RevenueReconciliationResponse
} from './revenue'

// ============================================
// Access Settings (user status defaults + Platform Manager switches)
// ============================================

export {
  UserStatusRoleSchema,
  UserStatusValueSchema,
  UserStatusConfigMapSchema,
  PlatformManagerSwitchStateSchema,
  PlatformManagerSwitchNameSchema,
  GetUserStatusConfigsRequestSchema,
  GetUserStatusConfigsDataSchema,
  GetUserStatusConfigsResponseSchema,
  UpdateUserStatusConfigsRequestSchema,
  UpdateUserStatusConfigsBodySchema,
  UpdateUserStatusConfigsResponseSchema,
  WaitingUserRoleSchema,
  GetWaitingUserCountsRequestSchema,
  WaitingUserCountsSchema,
  GetWaitingUserCountsResponseSchema,
  ActivateWaitingUsersRequestSchema,
  ActivateWaitingUsersBodySchema,
  ActivateWaitingUsersResponseSchema
} from './user-status-configs'

export type {
  UserStatusRole,
  UserStatusValue,
  UserStatusConfigMap,
  PlatformManagerSwitchState,
  PlatformManagerSwitchName,
  GetUserStatusConfigsRequest,
  GetUserStatusConfigsData,
  GetUserStatusConfigsResponse,
  UpdateUserStatusConfigsRequest,
  UpdateUserStatusConfigsBody,
  UpdateUserStatusConfigsResponse,
  WaitingUserRole,
  WaitingUserCounts,
  GetWaitingUserCountsRequest,
  GetWaitingUserCountsResponse,
  ActivateWaitingUsersRequest,
  ActivateWaitingUsersBody,
  ActivateWaitingUsersResponse
} from './user-status-configs'

export {
  PutEventEmailMessageIdsRequestSchema,
  EventEmailMessageIdProvenanceSchema,
  EventEmailMessageIdsResponseSchema
} from './event-email-message-ids'
export type {
  PutEventEmailMessageIdsRequest,
  EventEmailMessageIdProvenance,
  EventEmailMessageIdsResponse
} from './event-email-message-ids'

export {
  PutAttendanceEmailMessageIdsRequestSchema,
  AttendanceEmailMessageIdProvenanceSchema,
  AttendanceEmailMessageIdsResponseSchema
} from './attendance-email-message-ids'
export type {
  PutAttendanceEmailMessageIdsRequest,
  AttendanceEmailMessageIdProvenance,
  AttendanceEmailMessageIdsResponse
} from './attendance-email-message-ids'

// ============================================
// School feature health (super-admin + internal read/refresh)
// ============================================

export {
  FEATURE_HEALTH_SWEEP_INTERVAL_MINUTES,
  FEATURE_HEALTH_CLEARS_NEEDED,
  FEATURE_HEALTH_REFRESH_COOLDOWN_SECONDS,
  FEATURE_HEALTH_ERROR_TYPES,
  FEATURE_HEALTH_SCHOOL_STATUSES,
  FeatureHealthRefreshRefusalDetailsSchema,
  FeatureHealthParamsSchema,
  FeatureHealthSecondarySchema,
  FeatureHealthClearingSchema,
  FeatureHealthSubcomponentSchema,
  FeatureHealthSwitchSchema,
  FeatureHealthFeedingSyncSchema,
  FeatureHealthCoverageModuleSchema,
  FeatureHealthRowDetailSchema,
  FeatureHealthStoredReadingSchema,
  FeatureHealthRowSchema,
  FeatureHealthNamedRowSchema,
  FeatureHealthRollupSchema,
  FeatureHealthSchoolStatusSchema,
  FeatureHealthReadingSchema,
  FeatureHealthSchoolSchema,
  GetFeatureHealthResponseSchema,
  RefreshFeatureHealthResponseSchema
} from './feature-health'
export type {
  FeatureHealthRefreshRefusalDetails,
  FeatureHealthParams,
  FeatureHealthSecondary,
  FeatureHealthClearing,
  FeatureHealthSubcomponent,
  FeatureHealthSwitch,
  FeatureHealthFeedingSync,
  FeatureHealthCoverageModule,
  FeatureHealthRowDetail,
  FeatureHealthStoredReading,
  FeatureHealthRow,
  FeatureHealthNamedRow,
  FeatureHealthRollup,
  FeatureHealthSchoolStatus,
  FeatureHealthReading,
  FeatureHealthSchool,
  GetFeatureHealthResponse,
  RefreshFeatureHealthResponse
} from './feature-health'

// ============================================
// Release notes (super-admin read of Linear releases, SCH-2340)
// ============================================

export {
  RELEASE_NOTES_PAGE_SIZE,
  ReleaseNotesPipelineFilterSchema,
  ReleaseNotesPipelineSchema,
  ReleaseNotesIssueTypeSchema,
  ListReleaseNotesRequestSchema,
  ReleaseNotesIssueSchema,
  ReleaseNotesReleaseSchema,
  ListReleaseNotesResponseSchema
} from './release-notes'
export type {
  ReleaseNotesPipelineFilter,
  ReleaseNotesPipeline,
  ReleaseNotesIssueType,
  ListReleaseNotesRequest,
  ReleaseNotesIssue,
  ReleaseNotesRelease,
  ListReleaseNotesResponse
} from './release-notes'

// ============================================
// Calls dashboard (super-admin read of Stream Video calls)
// ============================================

export {
  CallsDashboardRangeSchema,
  GetCallsDashboardRequestSchema,
  CallMediaSchema,
  CallKindSchema,
  CallOutcomeSchema,
  CallerRoleSchema,
  CallsDashboardTotalsSchema,
  CallsDashboardDailyPointSchema,
  CallsDashboardSchoolRefSchema,
  CallsDashboardSchoolRowSchema,
  CallsDashboardCallerSchema,
  CallsDashboardAppViaSchema,
  CallsDashboardAppRefSchema,
  CallsStreamAppKindSchema,
  CallsDashboardCallSchema,
  CallsDashboardSourceSchema,
  CALLS_DASHBOARD_RECENT_LIMIT,
  CallsDashboardSchema,
  GetCallsDashboardResponseSchema
} from './calls-dashboard'
export type {
  CallsDashboardRange,
  GetCallsDashboardRequest,
  CallMedia,
  CallKind,
  CallOutcome,
  CallerRole,
  CallsDashboardTotals,
  CallsDashboardDailyPoint,
  CallsDashboardSchoolRef,
  CallsDashboardSchoolRow,
  CallsDashboardCaller,
  CallsDashboardAppVia,
  CallsDashboardAppRef,
  CallsStreamAppKind,
  CallsDashboardCall,
  CallsDashboardSource,
  CallsDashboard,
  GetCallsDashboardResponse
} from './calls-dashboard'
